import { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  ADMIN_WARNING_WINDOW_MS,
  clearAdminWarningDeadline,
  clearAdminSessionTokens,
  getAdminAccessToken,
  getAdminIdleRemainingMs,
  getAdminRefreshToken,
  getAdminWarningDeadline,
  hasAdminSessionTimedOutByInactivity,
  isAdminWarningGraceActive,
  isAdminSessionExtending,
  isAdminSessionRefreshDue,
  markAdminInteraction,
  markAdminSessionRefreshed,
  markAdminSessionTimedOut,
  setAdminWarningDeadline,
  setAdminSessionExtending,
  setAdminRedirectPath,
  setAdminSessionTokens,
  shouldWarnAdminSessionExpiry,
} from '@/lib/adminSession';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(getAdminAccessToken());
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(Math.ceil(ADMIN_WARNING_WINDOW_MS / 1000));
  const extendingSessionRef = useRef(false);

  const forceLocalLogout = () => {
    clearAdminSessionTokens();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setAdmin(null);
  };

  const handleSessionExpired = () => {
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentPath && currentPath !== '/login') {
      setAdminRedirectPath(currentPath);
    }
    markAdminSessionTimedOut();
    forceLocalLogout();
    clearAdminWarningDeadline();
    setWarningVisible(false);
    setSessionExpired(true);
  };

  const resetIdleWindow = async (options = {}) => {
    const { forceRefresh = false } = options;
    if (extendingSessionRef.current) {
      return;
    }
    setAdminSessionExtending(true);
    markAdminInteraction();
    clearAdminWarningDeadline();
    setWarningVisible(false);
    setWarningSecondsLeft(Math.ceil(ADMIN_WARNING_WINDOW_MS / 1000));
    if (forceRefresh || isAdminSessionRefreshDue()) {
      extendingSessionRef.current = true;
      const refreshToken = getAdminRefreshToken();
      if (!refreshToken) {
        extendingSessionRef.current = false;
        setAdminSessionExtending(false);
        return;
      }
      try {
        const refreshRes = await axios.post(`${API_URL}/api/admin/auth/refresh`, { refresh_token: refreshToken });
        const { access_token, refresh_token } = refreshRes.data;
        setAdminSessionTokens({ accessToken: access_token, refreshToken: refresh_token });
        markAdminSessionRefreshed();
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
      } finally {
        extendingSessionRef.current = false;
        setAdminSessionExtending(false);
      }
      return;
    }
    setAdminSessionExtending(false);
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (hasAdminSessionTimedOutByInactivity()) {
        if (!isAdminWarningGraceActive() && !isAdminSessionExtending()) {
          handleSessionExpired();
        }
        setLoading(false);
        return;
      }
      fetchAdmin();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchAdmin = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setAdmin(response.data);
      markAdminInteraction();
    } catch (error) {
      const refreshToken = getAdminRefreshToken();
      if (refreshToken) {
        try {
          const refreshRes = await axios.post(`${API_URL}/api/admin/auth/refresh`, { refresh_token: refreshToken });
          const { access_token, refresh_token } = refreshRes.data;
          setAdminSessionTokens({ accessToken: access_token, refreshToken: refresh_token });
          markAdminSessionRefreshed();
          setToken(access_token);
          const meRes = await axios.get(`${API_URL}/api/admin/auth/me`, {
            headers: { Authorization: `Bearer ${access_token}` },
          });
          setAdmin(meRes.data);
          return;
        } catch (_) {
          // fall through to logout
        }
      }
      handleSessionExpired();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, mfaCode = '') => {
    const response = await axios.post(`${API_URL}/api/admin/auth/login`, {
      username,
      password,
      mfa_code: mfaCode || undefined,
    });
    const { access_token, refresh_token, admin: adminData } = response.data;
    setAdminSessionTokens({ accessToken: access_token, refreshToken: refresh_token });
    markAdminInteraction();
    markAdminSessionRefreshed();
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setAdmin(adminData);
    setSessionExpired(false);
    return adminData;
  };

  const logout = () => {
    const accessToken = getAdminAccessToken();
    if (accessToken) {
      axios.post(`${API_URL}/api/admin/auth/logout`, null, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }).catch(() => undefined);
    }
    clearAdminSessionTokens();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setAdmin(null);
  };

  useEffect(() => {
    const onExpired = () => {
      handleSessionExpired();
    };
    window.addEventListener('admin-session-expired', onExpired);
    return () => window.removeEventListener('admin-session-expired', onExpired);
  }, []);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const handleUserInteraction = () => {
      if (sessionExpired) {
        return;
      }
      if (warningVisible) {
        resetIdleWindow({ forceRefresh: true }).catch(() => handleSessionExpired());
        return;
      }
      markAdminInteraction();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (hasAdminSessionTimedOutByInactivity()) {
        if (extendingSessionRef.current) {
          return;
        }
        if (!isAdminWarningGraceActive() && !isAdminSessionExtending()) {
          handleSessionExpired();
        }
        return;
      }
      if (warningVisible) {
        return;
      }
      markAdminInteraction();
    };

    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (hasAdminSessionTimedOutByInactivity()) {
        if (extendingSessionRef.current) {
          return;
        }
        if (!isAdminWarningGraceActive() && !isAdminSessionExtending()) {
          handleSessionExpired();
        }
        return;
      }
      const deadline = getAdminWarningDeadline();
      if (warningVisible && deadline) {
        const remaining = Math.max(0, deadline - Date.now());
        if (remaining <= 0) {
          if (extendingSessionRef.current || isAdminSessionExtending()) {
            return;
          }
          handleSessionExpired();
          return;
        }
        setWarningSecondsLeft(Math.max(1, Math.ceil(remaining / 1000)));
        return;
      }
      const remaining = getAdminIdleRemainingMs();
      if (shouldWarnAdminSessionExpiry()) {
        setAdminWarningDeadline(Date.now() + remaining);
        setWarningVisible(true);
        setWarningSecondsLeft(Math.max(1, Math.ceil(remaining / 1000)));
        return;
      }
      clearAdminWarningDeadline();
      setWarningVisible(false);
      setWarningSecondsLeft(Math.ceil(ADMIN_WARNING_WINDOW_MS / 1000));
    }, 1000);

    document.addEventListener('click', handleUserInteraction, true);
    document.addEventListener('keydown', handleUserInteraction, true);
    document.addEventListener('input', handleUserInteraction, true);
    document.addEventListener('touchstart', handleUserInteraction, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('click', handleUserInteraction, true);
      document.removeEventListener('keydown', handleUserInteraction, true);
      document.removeEventListener('input', handleUserInteraction, true);
      document.removeEventListener('touchstart', handleUserInteraction, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, warningVisible, sessionExpired]);

  const warningCountdownLabel = useMemo(() => {
    const minutes = Math.floor(warningSecondsLeft / 60);
    const seconds = warningSecondsLeft % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [warningSecondsLeft]);

  const hasPermission = (permission) => {
    if (!admin) return false;
    const permissions = admin.permissions || [];
    if (permissions.includes('all')) return true;
    return permissions.includes(permission);
  };

  return (
    <AuthContext.Provider value={{ admin, token, loading, login, logout, hasPermission }}>
      {children}
      {warningVisible && !sessionExpired ? (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-[#2D2420]/45 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-semibold text-[#2D2420]">Session Expiring Soon</h2>
            <p className="mt-3 text-sm text-[#5C4033]">
              Your session will expire in <span className="font-semibold text-[#C97A5E]">{warningCountdownLabel}</span>.
            </p>
            <p className="mt-2 text-sm text-[#5C4033]">
              Any activity will keep you logged in. You can also extend it now.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                className="border-[#D7B49E] text-[#5C4033]"
                onClick={() => handleSessionExpired()}
              >
                Logout now
              </Button>
              <Button
                className="bg-[#C97A5E] hover:bg-[#B06850]"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  resetIdleWindow({ forceRefresh: true }).catch(() => handleSessionExpired());
                }}
              >
                Keep me logged in
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      {sessionExpired ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#2D2420]/55 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-semibold text-[#2D2420]">Session Timed Out</h2>
            <p className="mt-3 text-sm text-[#5C4033]">
              Your session has timed out. Please login again to continue.
            </p>
            <div className="mt-6 flex justify-end">
              <Button
                className="bg-[#C97A5E] hover:bg-[#B06850]"
                onClick={() => {
                  setSessionExpired(false);
                  window.location.href = '/login';
                }}
              >
                Ok
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
